import React, { useState } from 'react';
import { ChevronDown, ChevronRight, LucideIcon } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

interface AdminSettingCardProps {
  /** Icon shown to the left of the title. */
  icon: LucideIcon;
  title: string;
  description: string;
  /** Called when the header is clicked. Omit when `children` is provided. */
  onClick?: () => void;
  /** Content revealed when the card is expanded; makes the card collapsible. */
  children?: React.ReactNode;
  'data-testid'?: string;
  /** Test id for the clickable header. */
  headerTestId?: string;
}

/**
 * Card used by the admin settings page: icon, title and description on the
 * left, chevron on the right. Acts as a navigation entry when given `onClick`,
 * or as a collapsible section when given `children`.
 */
export const AdminSettingCard: React.FC<AdminSettingCardProps> = ({
  icon: Icon,
  title,
  description,
  onClick,
  children,
  'data-testid': testId,
  headerTestId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const collapsible = children !== undefined;

  const handleClick = () => {
    if (collapsible) {
      setExpanded((prev) => !prev);
      return;
    }
    onClick?.();
  };

  return (
    <Card data-testid={testId}>
      <CardHeader className="p-0">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 p-6 text-left transition-colors hover:bg-muted/50"
          onClick={handleClick}
          aria-expanded={collapsible ? expanded : undefined}
          data-testid={headerTestId}
        >
          <div className="flex items-center gap-3">
            <Icon className="h-5 w-5 shrink-0 text-primary" />
            <div>
              <CardTitle>{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>
          {collapsible && expanded ? (
            <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
          )}
        </button>
      </CardHeader>

      {collapsible && expanded && <CardContent className="space-y-6">{children}</CardContent>}
    </Card>
  );
};
